import { MARKETING_SECTION_HEADING_HIGHLIGHT_INFO_MESSAGE } from '@modules/whitelabel-cover-page/marketing-page-section/marketing-page-section.constants';

export const STATS_LAYOUT_CONSTANTS = {
  LAYOUT_ID: 'stats-layout',
  LAYOUT_LABEL: 'Stats Cards',
  LAYOUT_IMAGE_PATH: 'https://cdn-www.topfans.com/TopFans/1785239301664.png',
  LAYOUT_DESCRIPTION: 'Three statistic cards with a section heading and subheading',
  DEFAULT_THEME_COLOR: '#00D2A6',
  BACKGROUND_IMAGE_ALT: 'Stats section background',
  LABEL_SECTION_HEADING: 'Section Heading',
  LABEL_SECTION_SUBHEADING: 'Section Subheading',
  LABEL_STAT_VALUE: 'Stat Value',
  LABEL_STAT_DESCRIPTION: 'Stat Description',
  LABEL_STAT_1_SECTION: 'Stat 1/3',
  LABEL_STAT_2_SECTION: 'Stat 2/3',
  LABEL_STAT_3_SECTION: 'Stat 3/3',
  PLACEHOLDER_ENTER_HEADING: 'Enter Heading',
  PLACEHOLDER_ENTER_SUBHEADING: 'Enter Subheading',
  PLACEHOLDER_ENTER_STAT_VALUE: 'Enter Stat Value',
  PLACEHOLDER_ENTER_STAT_DESCRIPTION: 'Enter Stat Description',
  INFO_HEADING_HIGHLIGHT: MARKETING_SECTION_HEADING_HIGHLIGHT_INFO_MESSAGE,
  ANIMATE_TOGGLE: {
    ENABLED_VALUE: 'enabled',
    LABEL: 'Animate Number',
    INFO: 'Counts the value up from zero when the card scrolls into view',
    ERROR_KEY: 'statValueMissingNumber',
    VALIDATION_MESSAGE:
      'Enter a number in Stat Value (Ex. 9, 10,000,000 or 100M+) to animate it',
    SAVE_ERROR:
      'Enter a number in every Stat Value set to animate, or turn its animation off.'
  }
} as const;

export const STATS_LAYOUT_STAT_INDEXES = [1, 2, 3] as const;

export type StatsLayoutStatIndex = (typeof STATS_LAYOUT_STAT_INDEXES)[number];

export function getStatValueFieldName(index: StatsLayoutStatIndex): string {
  return `stat_${index}_value`;
}

export function getStatAnimateFieldName(index: StatsLayoutStatIndex): string {
  return `stat_${index}_animate`;
}

/** Accepts checkbox arrays, booleans and strings; animation is off unless opted in. */
export function isStatValueAnimationEnabled(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.includes(STATS_LAYOUT_CONSTANTS.ANIMATE_TOGGLE.ENABLED_VALUE);
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return (
      normalized === 'true' ||
      normalized === STATS_LAYOUT_CONSTANTS.ANIMATE_TOGGLE.ENABLED_VALUE
    );
  }
  return false;
}

export function toStatValueAnimationToggleValue(value: unknown): string[] {
  return isStatValueAnimationEnabled(value)
    ? [STATS_LAYOUT_CONSTANTS.ANIMATE_TOGGLE.ENABLED_VALUE]
    : [];
}

/** Matches what the count-up directive can parse: any digit in the value. */
export function hasAnimatableNumber(value: unknown): boolean {
  return /\d/.test(String(value ?? ''));
}

export function isStatValueMissingAnimatableNumber(
  data: Record<string, unknown>,
  index: StatsLayoutStatIndex
): boolean {
  return (
    isStatValueAnimationEnabled(data[getStatAnimateFieldName(index)]) &&
    !hasAnimatableNumber(data[getStatValueFieldName(index)])
  );
}

/** Animation toggles are persisted as booleans; the form needs checkbox arrays. */
export function toStatsLayoutAnimationFormPatch(
  data: Record<string, unknown>
): Record<string, unknown> {
  const patch = { ...data };
  STATS_LAYOUT_STAT_INDEXES.forEach((index) => {
    const key = getStatAnimateFieldName(index);
    patch[key] = toStatValueAnimationToggleValue(patch[key]);
  });
  return patch;
}

export function toStatsLayoutAnimationPayload(
  data: Record<string, unknown>
): Record<string, unknown> {
  const payload = { ...data };
  STATS_LAYOUT_STAT_INDEXES.forEach((index) => {
    const key = getStatAnimateFieldName(index);
    payload[key] = isStatValueAnimationEnabled(payload[key]);
  });
  return payload;
}
